import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Paper,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from '@mui/material';
import { fetchRegionsBranches } from '../../../thunks/trainingThunks/thunk';

const PendencyModal = ({
  open,
  onClose,
  onSubmit,
  loading,
  reportType,
  availableRegions,
  availableBranches,
  trainingTypes,
  dispatch,
}) => {
  const [filters, setFilters] = useState({
    trainingType: 'all',
    regions: [],
    branches: [],
  });
  const [regionIds, setRegionIds] = useState([]);

  // Fetch branches when regions change
  useEffect(() => {
    if (regionIds.length > 0) {
      dispatch(fetchRegionsBranches({ regions: regionIds }));
    }
  }, [regionIds, dispatch]);

  const handleRegionChange = (event, value) => {
    setFilters((prev) => ({ ...prev, regions: value }));
    if (value.length === 0) {
      setFilters((prev) => ({ ...prev, branches: [] }));
      setRegionIds([]);
    } else {
      const regionIds = value.map((item) => item.id);
      setRegionIds(regionIds);
    }
  };

  const handleBranchChange = (event, value) => {
    setFilters((prev) => ({ ...prev, branches: value }));
  };

  const handleSelectAllRegions = () => {
    const allRegions = availableRegions || [];
    setFilters((prev) => ({ ...prev, regions: allRegions }));
    const regionIds = allRegions.map((item) => item.id);
    setRegionIds(regionIds);
  };

  const handleSelectAllBranches = () => {
    const allBranches = availableBranches || [];
    setFilters((prev) => ({ ...prev, branches: allBranches }));
  };

  const handleClearAllRegions = () => {
    setFilters((prev) => ({ ...prev, regions: [], branches: [] }));
    setRegionIds([]);
  };

  const handleClearAllBranches = () => {
    setFilters((prev) => ({ ...prev, branches: [] }));
  };

  const handleSubmit = () => {
    if (
      !filters.trainingType ||
      filters.trainingType === '' ||
      filters.regions.length === 0 ||
      filters.branches.length === 0
    ) {
      return false;
    }

    const payload = {
      trainingType:
        filters.trainingType === 'all'
          ? 'all'
          : filters.trainingType.trainingName || filters.trainingType,
      region: filters.regions,
      branches: filters.branches,
    };

    onSubmit(payload);
    return true;
  };

  const handleClose = () => {
    setFilters({
      trainingType: 'all',
      regions: [],
      branches: [],
    });
    setRegionIds([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {reportType?.title} Configuration
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Select filters to generate the {reportType?.title.toLowerCase()}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            Select Filters
          </Typography>

          <Grid container spacing={3}>
            {/* Training Type */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Training Type *
              </Typography>
              <Autocomplete
                options={[
                  { id: 'all', trainingName: 'All Training Types' },
                  ...(trainingTypes || []),
                ]}
                getOptionLabel={(option) => option.trainingName || ''}
                value={
                  filters.trainingType === 'all'
                    ? { id: 'all', trainingName: 'All Training Types' }
                    : filters.trainingType
                }
                onChange={(event, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    trainingType: value?.id === 'all' ? 'all' : value,
                  }))
                }
                PaperComponent={(paperProps) => {
                  const { children, ...restPaperProps } = paperProps;
                  return (
                    <Paper
                      {...restPaperProps}
                      style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#c1c1c1',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#a8a8a8',
                          },
                        },
                      }}
                    >
                      {children}
                    </Paper>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select training type"
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Regions */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Regions *
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleSelectAllRegions}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleClearAllRegions}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
              <Autocomplete
                multiple
                options={availableRegions || []}
                getOptionLabel={(option) => option.label || ''}
                value={filters.regions}
                onChange={handleRegionChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '65px !important',
                    maxHeight: '100px !important',
                    overflow: 'scroll !important',
                  },
                }}
                PaperComponent={(paperProps) => {
                  const { children, ...restPaperProps } = paperProps;
                  return (
                    <Paper
                      {...restPaperProps}
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#c1c1c1',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#a8a8a8',
                          },
                        },
                      }}
                    >
                      {children}
                    </Paper>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select regions"
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))
                }
              />
            </Grid>

            {/* Branches */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Branches *
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleSelectAllBranches}
                    disabled={filters.regions.length === 0}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleClearAllBranches}
                    disabled={filters.regions.length === 0}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
              <Autocomplete
                multiple
                options={availableBranches || []}
                getOptionLabel={(option) => option.label || ''}
                value={filters.branches}
                onChange={handleBranchChange}
                disabled={filters.regions.length === 0}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '65px !important',
                    maxHeight: '100px !important',
                    overflow: 'scroll !important',
                  },
                }}
                PaperComponent={(paperProps) => {
                  const { children, ...restPaperProps } = paperProps;
                  return (
                    <Paper
                      {...restPaperProps}
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#c1c1c1',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#a8a8a8',
                          },
                        },
                      }}
                    >
                      {children}
                    </Paper>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      filters.regions.length === 0 ? 'Select regions first' : 'Select branches'
                    }
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>

          {/* Action Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                loading ||
                !filters.trainingType ||
                filters.trainingType === '' ||
                filters.regions.length === 0 ||
                filters.branches.length === 0
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                },
              }}
            >
              {loading ? 'Generating Report...' : `Generate ${reportType?.title}`}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendencyModal;
